'use client';

import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Typography } from '@mui/material';
import { Zap } from 'lucide-react';

import { useT } from '@/hooks';
import type { StreakStatus } from '@/types/streak.types';

// Mirrors DAILY_VOLUME_TIERS in porvenix_be/src/v1/components/streak/streak.service.js —
// keep these two in sync if the tier schedule ever changes.
const DAILY_VOLUME_TIERS: Record<number, number> = {
  1: 10,
  2: 10,
  3: 25,
  4: 25,
  5: 50,
  6: 50,
  7: 100,
};

interface StreakCycleTrackerProps {
  data: StreakStatus;
}

/**
 * StreakCycleTracker
 * The single most important teaching moment on this page: streaks run in a
 * repeating 7-day cycle where the daily trading target climbs from $10 to
 * $100, and day 7 pays a big points bonus on top. Rendered as a 7-cell strip
 * so the escalating-reward shape is obvious at a glance, with today's cell
 * showing live $ progress toward its target.
 */
export const StreakCycleTracker: React.FC<StreakCycleTrackerProps> = ({ data }) => {
  const { t } = useT();
  const { cycleDay, todaysVolume, todaysTarget, todaysProgressPct, isAtRisk } = data;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 4,
        bgcolor: 'background.paper',
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {t('streaks.cycleTitle', { defaultValue: "This cycle's targets" })}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {t('streaks.cycleDayLabel', { defaultValue: `Day ${cycleDay} of 7` })}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
        {t('streaks.cycleSubtitle', {
          defaultValue:
            "Trade to hit each day's target and the target grows through the week. Clear day 7 and the cycle resets — with a points bonus.",
        })}
      </Typography>

      {/* 7-day strip */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: { xs: 0.75, sm: 1.25 },
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7].map((day) => {
          const state = day < cycleDay ? 'done' : day === cycleDay ? 'today' : 'upcoming';
          const target = DAILY_VOLUME_TIERS[day];
          const isBonusDay = day === 7;

          return (
            <Box
              key={day}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                py: { xs: 1.25, sm: 1.75 },
                px: 0.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: state === 'today' ? (isAtRisk ? 'error.main' : '#e8b046') : 'divider',
                bgcolor:
                  state === 'done'
                    ? 'rgba(249,115,22,0.10)'
                    : state === 'today'
                      ? isAtRisk
                        ? 'rgba(224, 102, 79, 0.08)'
                        : 'rgba(249,115,22,0.06)'
                      : 'transparent',
                opacity: state === 'upcoming' ? 0.55 : 1,
              }}
            >
              {isBonusDay && (
                <Zap
                  size={12}
                  color="#e8b046"
                  fill="#e8b046"
                  style={{ position: 'absolute', top: 6, right: 6 }}
                />
              )}
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: state === 'today' ? 'text.primary' : 'text.secondary',
                }}
              >
                {t('streaks.cycleDayShort', { defaultValue: `Day ${day}` })}
              </Typography>

              {state === 'done' ? (
                <CheckIcon sx={{ fontSize: 20, color: '#e8b046' }} />
              ) : (
                <Typography
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.95rem' },
                    fontWeight: 800,
                    color: state === 'today' ? 'text.primary' : 'text.secondary',
                  }}
                >
                  ${target}
                </Typography>
              )}

              {isBonusDay && (
                <Typography
                  sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#e8b046', lineHeight: 1 }}
                >
                  {t('streaks.cycleBonusTag', { defaultValue: '+500 pts' })}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Today's live progress */}
      <Box sx={{ mt: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {t('streaks.cycleTodayProgress', {
              defaultValue: `$${todaysVolume.toFixed(2)} of $${todaysTarget.toFixed(2)} traded today`,
            })}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: isAtRisk ? 'error.main' : '#e8b046', fontWeight: 800 }}
          >
            {isAtRisk
              ? t('streaks.atRisk', { defaultValue: 'At risk' })
              : `${Math.min(100, todaysProgressPct).toFixed(0)}%`}
          </Typography>
        </Box>
        <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              width: `${Math.max(0, Math.min(100, todaysProgressPct))}%`,
              borderRadius: 4,
              background: 'linear-gradient(90deg, #e8b046, #e0664f)',
              transition: 'width 0.6s ease',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
